/**
 * ChildEventTab — one nested event's pane on an ancestor's Event Detail page: any event beneath the
 * page's event, at any depth, opened from the page's navigator (EventTreeNav).
 *
 * Re-reads the child with GetEvent rather than rendering the copy ListSubEvents returned: a listing
 * returns the description's file references unsigned (AWSECEventHub api-integration-guide), so its
 * images would not load; GetEvent signs them. Then renders the same cards as the child's own page
 * (EventDetailBody), which loads the child's attendees itself. Its Parent Event is the nearest event
 * above it in the tree — the page's event only for a direct child — and comes from the navigator's
 * tree, so it is not read again; the Event Hierarchy field is left to Overall (see EventDetailBody).
 *
 * Mounted only once its row is first selected, then kept, so a tree with many events does not read
 * them all up front and switching back does not reload.
 */

import { useEffect, useState } from 'react';
import { Alert, Box, StatusIndicator } from '@amzn/awsui-components-react';
import { eventHubCalendarClient, type Event } from '../../client/EventCalendarClient';
import EventDetailBody, { type EventDetailBodyProps } from './EventDetailBody';

export interface ChildEventTabProps {
  eventId: string;
  /** The nearest event above this one — shown as its Parent Event without a re-read. */
  parent: { eventId: string; eventName?: string };
  roles: EventDetailBodyProps['roles'];
  login: string;
}

export default function ChildEventTab({ eventId, parent, roles, login }: ChildEventTabProps) {
  const [event, setEvent] = useState<Event | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setEvent(null);
    setError(null);
    eventHubCalendarClient()
      .getEvent(eventId, { signal: controller.signal })
      .then(setEvent, (e: unknown) => {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
      });
    return () => controller.abort();
  }, [eventId]);

  if (error) {
    return (
      <Box padding={{ top: 'l' }}>
        <Alert type="error" header="Failed to load this child event">
          {error}
        </Alert>
      </Box>
    );
  }
  if (!event) {
    return (
      <Box padding={{ top: 'l' }}>
        <StatusIndicator type="loading">Loading</StatusIndicator>
      </Box>
    );
  }
  return (
    <EventDetailBody
      event={event}
      roles={roles}
      login={login}
      knownParent={parent}
      pageOverlay={false}
      showHierarchy={false}
    />
  );
}
